<template>
  <div class="w-full">
    <select class="hidden">
      <option :value="activeOption || ''">
        {{ activeOption }}
      </option>
    </select>

    <button
      class="relative flex h-14 w-full grow cursor-pointer items-center justify-start rounded-lg border px-4 text-left text-base transition-colors hover:border-gray-400"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      @click="onClick"
    >
      <div v-if="activeOption" class="flex w-full justify-between">
        <slot name="selected-option" :option="activeOption">
          <div class="flex w-full flex-col justify-between">
            <div
              class="text-sm text-black"
              :class="
                !activeOption?.quantityAvailable
                  ? 'text-gray-400 line-through'
                  : 'text-black'
              "
            >
              {{ activeOption.title }}
            </div>

            <StockIndicator
              class="flex text-xs"
              :in-stock="activeOption.quantityAvailable"
            />
          </div>
        </slot>
      </div>
      <div v-else>{{ placeholder }}</div>
      <IconChevron
        :direction="isOpen ? 'up' : 'down'"
        class="pointer-events-none relative text-2xl"
      />
    </button>

    <Modal
      v-model:active="isOpen"
      size="md"
      :always-on-top="true"
      :close-button="false"
    >
      <div class="relative isolate flex flex-col">
        <div class="sticky top-0 z-10 border-b bg-white p-4 font-bold">
          {{ heading }}
          <ButtonIcon
            :label="t('global.accessibility.close')"
            theme="light"
            :tabindex="-1"
            class="absolute right-2 top-2 z-20"
            @click="() => (isOpen = false)"
          >
            <IconX class="text-2xl text-black" />
          </ButtonIcon>
        </div>
        <div class="h-full overflow-y-auto">
          <slot name="options" :on-option-click="onOptionClick">
            <VariantSelectorList
              :options="options"
              :active="activeOption"
              @click="onOptionClick"
            />
          </slot>
        </div>
      </div>
    </Modal>
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue';

import ButtonIcon from '@/shared/components/UI/Buttons/ButtonIcon.vue';
import IconChevron from '@/shared/components/UI/Icons/IconChevron.vue';
import IconX from '@/shared/components/UI/Icons/IconX.vue';
import Modal from '@/shared/components/UI/Modal.vue';
import StockIndicator from '@/shared/components/UI/StockIndicator.vue';
import { Variant } from '@/shared/composables/patternProducts/patternProducts.types';
import { useTranslations } from '@/shared/composables/useTranslations';
import { useViewport } from '@/shared/composables/useViewport';

import VariantSelectorList from './VariantSelectorList.vue';

const { t } = useTranslations();

const props = withDefaults(
  defineProps<{
    heading?: string;
    options?: Variant[];
    active: Variant | null;
    placeholder?: string;
  }>(),
  {
    heading: '',
    options: () => [],
    placeholder: ''
  }
);

const isOpen = ref(false);
const activeOption = ref<Variant | null>(props.active);

const { isMobile } = useViewport();

watch(isMobile, (newVal) => {
  if (!newVal) {
    isOpen.value = false;
  }
});

watch(
  () => props.active,
  (newVal) => {
    activeOption.value = newVal;
  }
);

const onClick = () => {
  isOpen.value = true;
};

const emit = defineEmits(['option-click']);

const onOptionClick = (option: Variant) => {
  activeOption.value = option;
  isOpen.value = false;
  emit('option-click', option);
};
</script>
